import React, { useEffect, useState } from 'react';
import {
    Alert, Box, Chip, CircularProgress, Container, Divider, Paper, Typography,
} from '@mui/material';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import MilitaryTechIcon from '@mui/icons-material/MilitaryTech';
import SportsScoreIcon from '@mui/icons-material/SportsScore';
import PageLayout from '../components/PageLayout';
import { useAuth } from '../context/AuthContext';
import { useNotification } from '../context/NotificationContext';
import { fetchUserTrophies, medalForPosition } from '../services/history';
import { page } from '../theme/commonStyles';

// Bekerkleuren: goud, zilver, brons. Bewust hardcoded — geen thematokens voor edelmetaal.
const MEDAL_COLOR = '#B08D57';
const CUP_COLORS = { gold: '#FFD700', silver: '#C0C0C0', bronze: '#CD7F32' };
const POSITION_LABELS = { 1: '1e plaats', 2: '2e plaats', 3: '3e plaats' };

/** Een grote telkaart per prijssoort in de vitrine-samenvatting. */
function TrophyTile({ icon, color, count, label }) {
    const active = count > 0;
    return (
        <Paper
            elevation={active ? 3 : 0}
            sx={{
                p: 2, textAlign: 'center', minWidth: 110, flex: '1 1 110px',
                opacity: active ? 1 : 0.45,
                border: '1px solid', borderColor: 'divider',
            }}
        >
            {React.cloneElement(icon, { sx: { fontSize: 44, color } })}
            <Typography variant="h5" sx={{ fontWeight: 'bold', mt: 0.5 }}>{count}</Typography>
            <Typography variant="body2" color="text.secondary">{label}</Typography>
        </Paper>
    );
}

/**
 * De persoonlijke prijzenkast: alle behaalde bekers en medailles van de ingelogde
 * gebruiker, met per seizoen wat er gewonnen is.
 */
function TrophyCabinet() {
    const { user } = useAuth();
    const notify = useNotification();
    const [data, setData] = useState({ seasons: [], totals: { gold: 0, silver: 0, bronze: 0, medals: 0, finales: 0 } });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!user) return undefined;
        let cancelled = false;
        fetchUserTrophies(user.uid)
            .then((result) => { if (!cancelled) setData(result); })
            .catch((err) => {
                console.error('Kon de prijzenkast niet ophalen:', err);
                if (!cancelled) notify.error('De prijzenkast kon niet opgehaald worden.');
            })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [user, notify]);

    if (loading) {
        return (
            <PageLayout>
                <Container sx={page.loadingContainer}><CircularProgress /></Container>
            </PageLayout>
        );
    }

    const { seasons, totals } = data;
    const heeftPrijzen = totals.gold || totals.silver || totals.bronze || totals.medals || totals.finales;

    return (
        <PageLayout>
            <Container component="main" maxWidth="md">
                <Box sx={{ mt: 4, mb: 6 }}>
                    <Typography component="h1" variant="h4" gutterBottom>
                        Prijzenkast
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Je behaalde bekers en medailles over alle seizoenen.
                    </Typography>

                    {seasons.length === 0 ? (
                        <Alert severity="info">
                            Er zijn voor jou nog geen resultaten vastgelegd.
                        </Alert>
                    ) : (
                        <>
                            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 4 }}>
                                <TrophyTile icon={<EmojiEventsIcon />} color={CUP_COLORS.gold} count={totals.gold} label="Goud" />
                                <TrophyTile icon={<EmojiEventsIcon />} color={CUP_COLORS.silver} count={totals.silver} label="Zilver" />
                                <TrophyTile icon={<EmojiEventsIcon />} color={CUP_COLORS.bronze} count={totals.bronze} label="Brons" />
                                <TrophyTile icon={<MilitaryTechIcon />} color={MEDAL_COLOR} count={totals.medals} label="Weekprijzen" />
                                <TrophyTile icon={<SportsScoreIcon />} color="text.secondary" count={totals.finales} label="Finaleplekken" />
                            </Box>

                            {!heeftPrijzen && (
                                <Alert severity="info" sx={{ mb: 3 }}>
                                    Nog geen bekers of medailles — maar je deelnames staan hieronder.
                                </Alert>
                            )}

                            <Typography variant="h6" gutterBottom>Per seizoen</Typography>
                            <Paper variant="outlined">
                                {seasons.map((s, index) => {
                                    const cup = medalForPosition(s.position);
                                    return (
                                        <React.Fragment key={s.id}>
                                            {index > 0 && <Divider />}
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, flexWrap: 'wrap' }}>
                                                <Box sx={{ flexGrow: 1, minWidth: 160 }}>
                                                    <Typography sx={{ fontWeight: 'bold' }}>{s.seasonName || s.seasonId}</Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {POSITION_LABELS[s.position] || (s.position ? `${s.position}e plaats` : 'Geen finaleplek')}
                                                    </Typography>
                                                </Box>
                                                {cup && (
                                                    <EmojiEventsIcon sx={{ color: CUP_COLORS[cup], fontSize: 32 }} />
                                                )}
                                                {s.weekPrizes > 0 && (
                                                    <Chip
                                                        icon={<MilitaryTechIcon sx={{ color: `${MEDAL_COLOR} !important` }} />}
                                                        label={`${s.weekPrizes} weekprij${s.weekPrizes === 1 ? 's' : 'zen'}`}
                                                        variant="outlined"
                                                        size="small"
                                                    />
                                                )}
                                            </Box>
                                        </React.Fragment>
                                    );
                                })}
                            </Paper>
                        </>
                    )}
                </Box>
            </Container>
        </PageLayout>
    );
}

export default TrophyCabinet;
